import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Plus, Car, Calendar, Gauge, TrendingUp, Trash2, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEstimations();
  }, []);

  const fetchEstimations = async () => {
    try {
      const res = await axios.get(`${API}/estimations`, { withCredentials: true });
      setEstimations(res.data.estimations || []);
    } catch (err) {
      console.error("Fetch estimations error:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEstimation = async (estimationId) => {
    try {
      await axios.delete(`${API}/estimations/${estimationId}`, { withCredentials: true });
      setEstimations(prev => prev.filter(e => e.estimation_id !== estimationId));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Header user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Welcome & Action */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="dashboard-title"
            >
              Mes estimations
            </h1>
            <p className="text-slate-600 mt-1">
              {estimations.length > 0
                ? `${estimations.length} estimation${estimations.length > 1 ? 's' : ''} realisee${estimations.length > 1 ? 's' : ''}`
                : "Aucune estimation pour le moment"}
            </p>
          </div>
          <Button
            onClick={() => navigate("/new-estimation")}
            className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium px-6 py-3 h-auto btn-press"
            data-testid="new-estimation-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle estimation
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : estimations.length === 0 ? (
          /* Empty state */
          <div className="text-center py-20 animate-fade-in-up" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <Car className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Aucune estimation
            </h3>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Commencez par estimer le prix de votre premier vehicule. C'est rapide et gratuit.
            </p>
            <Button
              onClick={() => navigate("/new-estimation")}
              className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
              data-testid="empty-new-estimation-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Estimer un vehicule
            </Button>
          </div>
        ) : (
          /* Estimation cards - Bento grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="estimations-grid">
            {estimations.map((est, i) => (
              <div
                key={est.estimation_id}
                className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden card-hover animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`estimation-card-${est.estimation_id}`}
              >
                {/* Card header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {est.brand} {est.model}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {est.year}
                        </span>
                        <span className="flex items-center gap-1">
                          <Gauge className="w-3.5 h-3.5" />
                          {new Intl.NumberFormat("fr-FR").format(est.mileage)} km
                        </span>
                      </div>
                    </div>
                    <Badge
                      variant={est.status === "completed" ? "default" : est.status === "processing" ? "secondary" : "destructive"}
                      className={est.status === "completed" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}
                    >
                      {est.status === "completed" ? "Termine" : est.status === "processing" ? "En cours" : "Erreur"}
                    </Badge>
                  </div>

                  {/* Price */}
                  {est.status === "completed" && est.result?.estimation_moyenne && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Estimation moyenne</p>
                      <p
                        className="text-2xl font-bold text-slate-900"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        data-testid={`estimation-price-${est.estimation_id}`}
                      >
                        {formatPrice(est.result.estimation_moyenne)}
                      </p>
                    </div>
                  )}

                  {/* Meta */}
                  <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
                    <span>{formatDate(est.created_at)}</span>
                    <span>-</span>
                    <span>{est.fuel_type}</span>
                    <span>-</span>
                    <span>{est.transmission}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/estimation/${est.estimation_id}`)}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid={`view-estimation-${est.estimation_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir details
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteEstimation(est.estimation_id)}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`delete-estimation-${est.estimation_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
